import {
  Close as CloseIcon,
  FlashOn as EnergyIcon,
  FilterAlt as FilterIcon,
  AccountTree as JourneyIcon,
  Grade as PriorityIcon,
  AccountBalance as AutogiroIcon,
  EventBusy as DeliveryEndIcon,
  Receipt as ReceiptIcon,
  FormatSize as SizeIcon,
} from "@mui/icons-material";
import { Chip, Stack, IconButton, Tooltip } from "@mui/material";
import React from "react";
import { formatEnergyWithScaling } from "../../../utils/numberFormatters";
import { getDeliveryEndFilterLabel } from "./dateUtils";
import { getDeliveryEndReasonLabel } from "./deliveryEndReasons";
// Filter types
export type SegmentFilter = string | null;
export type SegmentImportanceFilter = string | null;
export type SegmentSizeFilter = string | null;
export type ActivityFilter = "all" | "logged-in" | "logged-in-7d" | "logged-in-30d" | "logged-in-90d" | "no-login" | "no-contact";
export type ActionFilter =
  | "all"
  | "has-next-action"
  | "no-next-action"
  | "has-last-action"
  | "no-last-action"
  | "last-action-6m"
  | "last-action-3m";
export type ContactFilter = "all" | "has-contact" | "no-contact";
export type JourneyTypeFilter = string | null;
export type ConsumptionFilter =
  | "all"
  | "low"
  | "medium"
  | "high"
  | { min: number; max: number };
export type InvoiceDeliveryFilter = string | null;
export type DirectDebitFilter = "all" | "yes" | "no";
// Datum i formatet yyyy-MM-dd, null = öppen gräns
export type DeliveryEndFilter = { from: string | null; to: string | null } | null;
// Orsakskod (CustomerDeliveryEndReasonId), NO_DELIVERY_END_REASON = kunder utan orsak
export type DeliveryEndReasonFilter = string | null;

interface FilterButtonProps {
  segmentFilter: SegmentFilter;
  setSegmentFilter: (filter: SegmentFilter) => void;
  segmentImportanceFilter: SegmentImportanceFilter;
  setSegmentImportanceFilter: (filter: SegmentImportanceFilter) => void;
  segmentSizeFilter: SegmentSizeFilter;
  setSegmentSizeFilter: (filter: SegmentSizeFilter) => void;
  activityFilter: ActivityFilter;
  setActivityFilter: (filter: ActivityFilter) => void;
  actionFilter: ActionFilter;
  setActionFilter: (filter: ActionFilter) => void;
  contactFilter: ContactFilter;
  setContactFilter: (filter: ContactFilter) => void;
  journeyTypeFilter?: JourneyTypeFilter;
  setJourneyTypeFilter?: (filter: JourneyTypeFilter) => void;
  consumptionFilter?: ConsumptionFilter;
  setConsumptionFilter?: (filter: ConsumptionFilter) => void;
  invoiceDeliveryFilter?: InvoiceDeliveryFilter;
  setInvoiceDeliveryFilter?: (filter: InvoiceDeliveryFilter) => void;
  directDebitFilter?: DirectDebitFilter;
  setDirectDebitFilter?: (filter: DirectDebitFilter) => void;
  deliveryEndFilter?: DeliveryEndFilter;
  setDeliveryEndFilter?: (filter: DeliveryEndFilter) => void;
  deliveryEndReasonFilter?: DeliveryEndReasonFilter;
  setDeliveryEndReasonFilter?: (filter: DeliveryEndReasonFilter) => void;
  onOpenFilter: (event: React.MouseEvent<HTMLElement>) => void;
  onClearAllFilters?: () => void;
}

const FilterButton: React.FC<FilterButtonProps> = ({
  segmentFilter,
  setSegmentFilter,
  segmentImportanceFilter,
  setSegmentImportanceFilter,
  segmentSizeFilter,
  setSegmentSizeFilter,
  activityFilter,
  setActivityFilter,
  actionFilter,
  setActionFilter,
  contactFilter,
  setContactFilter,
  journeyTypeFilter = null,
  setJourneyTypeFilter = () => {},
  consumptionFilter = "all",
  setConsumptionFilter = () => {},
  invoiceDeliveryFilter = null,
  setInvoiceDeliveryFilter = () => {},
  directDebitFilter = "all",
  setDirectDebitFilter = () => {},
  deliveryEndFilter = null,
  setDeliveryEndFilter = () => {},
  deliveryEndReasonFilter = null,
  setDeliveryEndReasonFilter = () => {},
  onOpenFilter,
  onClearAllFilters,
}) => {
  // Format consumption filter label
  const getConsumptionFilterLabel = () => {
    if (typeof consumptionFilter === "object" && consumptionFilter !== null) {
      return `Förbrukning: ${formatEnergyWithScaling(
        consumptionFilter.min
      )} - ${formatEnergyWithScaling(consumptionFilter.max)}`;
    }
    switch (consumptionFilter) {
      case "low":
        return "Låg förbrukning";
      case "medium":
        return "Medel förbrukning";
      case "high":
        return "Hög förbrukning";
      default:
        return "";
    }
  };

  // Format action filter label
  const getActionFilterLabel = () => {
    switch (actionFilter) {
      case "has-next-action":
        return "Har nästa aktivitet";
      case "no-next-action":
        return "Saknar nästa aktivitet";
      case "has-last-action":
        return "Har senaste aktivitet";
      case "no-last-action":
        return "Saknar senaste aktivitet";
      case "last-action-6m":
        return "Haft senaste 6 månaderna";
      case "last-action-3m":
        return "Haft senaste 3 månaderna";
      default:
        return "";
    }
  };

  // Format contact filter label
  const getContactFilterLabel = () => {
    switch (contactFilter) {
      case "has-contact":
        return "Har kontaktperson";
      case "no-contact":
        return "Saknar kontaktperson";
      default:
        return "";
    }
  };

  // Count active filters
  const activeFilterCount = [
    segmentFilter !== null,
    segmentImportanceFilter !== null,
    segmentSizeFilter !== null,
    journeyTypeFilter !== null,
    consumptionFilter !== "all",
    activityFilter !== "all",
    actionFilter !== "all",
    contactFilter !== "all",
    invoiceDeliveryFilter !== null,
    directDebitFilter !== "all",
    deliveryEndFilter !== null,
    deliveryEndReasonFilter !== null,
  ].filter(Boolean).length;

  return (
    <Stack alignItems={"flex-end"} flexDirection={"column"} spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          icon={<FilterIcon />}
          label={
            activeFilterCount > 0 ? `Filter (${activeFilterCount})` : "Filter"
          }
          onClick={onOpenFilter}
          variant="outlined"
          color="primary"
          sx={{
            ...(activeFilterCount > 0 && {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "& .MuiChip-icon": {
                color: "primary.contrastText",
              },
            }),
          }}
        />
        {activeFilterCount > 0 && onClearAllFilters && (
          <Tooltip title="Rensa alla filter" placement="top">
            <IconButton
              size="small"
              onClick={onClearAllFilters}
              sx={{
                bgcolor: "error.light",
                color: "error.contrastText",
                "&:hover": {
                  bgcolor: "error.main",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Quick filters as horizontal chips */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
        {/* Whole segment filter chip */}
        {segmentFilter !== null && (
          <Chip
            label={segmentFilter}
            size="small"
            onDelete={() => setSegmentFilter(null)}
            color="primary"
          />
        )}

        {/* Segment importance level filter chip */}
        {segmentImportanceFilter !== null && (
          <Chip
            icon={<PriorityIcon fontSize="small" />}
            label={`Nivå ${segmentImportanceFilter}`}
            size="small"
            onDelete={() => setSegmentImportanceFilter(null)}
            color="primary"
          />
        )}

        {/* Segment size filter chip */}
        {segmentSizeFilter !== null && (
          <Chip
            icon={<SizeIcon fontSize="small" />}
            label={`Storlek ${segmentSizeFilter}`}
            size="small"
            onDelete={() => setSegmentSizeFilter(null)}
            color="primary"
          />
        )}

        {/* Journey type filter chip */}
        {journeyTypeFilter !== null && (
          <Chip
            icon={<JourneyIcon fontSize="small" />}
            label={journeyTypeFilter}
            size="small"
            onDelete={() => setJourneyTypeFilter(null)}
            color="primary"
          />
        )}

        {/* Consumption filter chip */}
        {consumptionFilter !== "all" && (
          <Chip
            icon={<EnergyIcon fontSize="small" />}
            label={getConsumptionFilterLabel()}
            size="small"
            onDelete={() => setConsumptionFilter("all")}
            color="primary"
          />
        )}

        {/* Action filter chip */}
        {actionFilter !== "all" && (
          <Chip
            label={getActionFilterLabel()}
            size="small"
            onDelete={() => setActionFilter("all")}
            color="primary"
          />
        )}

        {/* Contact filter chip */}
        {contactFilter !== "all" && (
          <Chip
            label={getContactFilterLabel()}
            size="small"
            onDelete={() => setContactFilter("all")}
            color="primary"
          />
        )}

        {/* Invoice delivery filter chip */}
        {invoiceDeliveryFilter !== null && (
          <Chip
            icon={<ReceiptIcon fontSize="small" />}
            label={invoiceDeliveryFilter}
            size="small"
            onDelete={() => setInvoiceDeliveryFilter(null)}
            color="primary"
          />
        )}

        {/* Direct debit filter chip */}
        {directDebitFilter !== "all" && (
          <Chip
            icon={<AutogiroIcon fontSize="small" />}
            label={directDebitFilter === "yes" ? "Autogiro" : "Ej autogiro"}
            size="small"
            onDelete={() => setDirectDebitFilter("all")}
            color="primary"
          />
        )}

        {/* Delivery end date filter chip */}
        {deliveryEndFilter !== null && (
          <Chip
            icon={<DeliveryEndIcon fontSize="small" />}
            label={getDeliveryEndFilterLabel(deliveryEndFilter)}
            size="small"
            onDelete={() => setDeliveryEndFilter(null)}
            color="primary"
          />
        )}

        {/* Delivery end reason filter chip */}
        {deliveryEndReasonFilter !== null && (
          <Chip
            icon={<DeliveryEndIcon fontSize="small" />}
            label={`Orsak: ${getDeliveryEndReasonLabel(deliveryEndReasonFilter)}`}
            size="small"
            onDelete={() => setDeliveryEndReasonFilter(null)}
            color="primary"
          />
        )}

        {/* Activity filter chip */}
        {activityFilter !== "all" && (
          <Chip
            label={
              activityFilter === "logged-in"
                ? "Har loggat in"
                : activityFilter === "logged-in-7d"
                ? "Inloggad senaste 7 dagar"
                : activityFilter === "logged-in-30d"
                ? "Inloggad senaste 30 dagar"
                : activityFilter === "logged-in-90d"
                ? "Inloggad senaste 90 dagar"
                : activityFilter === "no-login"
                ? "Aldrig loggat in"
                : "Ej kontaktad"
            }
            size="small"
            onDelete={() => setActivityFilter("all")}
            color="primary"
          />
        )}
      </Stack>
    </Stack>
  );
};

export default FilterButton;
